import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { AnnouncementBar } from "../components/AnnouncementBar";
import { MarketingLayout } from "../components/MarketingLayout";
import { 
  BookOpen, 
  Search, 
  ChevronRight,
  Home,
  CreditCard,
  PieChart,
  Receipt,
  Calendar,
  TrendingUp,
  Target,
  Wallet,
  LineChart,
  GraduationCap,
  Users,
  Settings,
  Shield,
  HelpCircle,
  ArrowLeft,
  ExternalLink
} from "lucide-react";

const docSections = [
  {
    id: "getting-started",
    title: "Getting Started",
    icon: Home,
    description: "Create your account and connect your bank",
    topics: ["Creating Your Account", "Your First Login", "Connecting Banks"]
  },
  {
    id: "dashboard",
    title: "Dashboard Overview",
    icon: PieChart,
    description: "Understand your financial command center",
    topics: ["Key Metrics", "Dashboard Cards", "Quick Actions"]
  },
  {
    id: "bank-connection",
    title: "Connecting Your Bank",
    icon: CreditCard,
    description: "Securely link your Canadian bank accounts",
    topics: ["Supported Banks", "How to Connect", "Sync Frequency", "Troubleshooting"]
  },
  {
    id: "transactions",
    title: "Transactions",
    icon: CreditCard,
    description: "View, search, and categorize transactions",
    topics: ["Viewing Transactions", "Filtering & Search", "Categories", "Manual Transactions"]
  },
  {
    id: "budget",
    title: "Budget Management",
    icon: PieChart,
    description: "Create and track spending budgets",
    topics: ["Setting Up Budgets", "Tracking Progress", "Budget Alerts"]
  },
  {
    id: "bills",
    title: "Bills & Payment Tracking",
    icon: Receipt,
    description: "Manage recurring bills and track payments",
    topics: ["Automatic Detection", "Payment Tracking", "Adding Bills", "Calendar Sync"],
    isNew: true
  },
  {
    id: "calendar",
    title: "Calendar Integration",
    icon: Calendar,
    description: "Sync bills to Apple, Google, or Outlook",
    topics: ["Supported Calendars", "Setup Instructions", "Troubleshooting"]
  },
  {
    id: "monthly-plan",
    title: "Monthly Plan",
    icon: Calendar,
    description: "See your month-end financial snapshot",
    topics: ["Key Metrics", "Upcoming Bills", "Cash Flow Timeline"]
  },
  {
    id: "insights",
    title: "Insights & AI Predictions",
    icon: TrendingUp,
    description: "AI-powered spending analysis and forecasts",
    topics: ["AI Summaries", "Spending Predictions", "Key Insights"],
    isNew: true
  },
  {
    id: "goals",
    title: "Financial Goals",
    icon: Target,
    description: "Set and track SMART financial goals",
    topics: ["Creating Goals", "Goal Types", "Tracking Progress"]
  },
  {
    id: "net-worth",
    title: "Net Worth Tracking",
    icon: Wallet,
    description: "Monitor your overall financial health",
    topics: ["Assets & Liabilities", "Historical Tracking", "Manual Entries"]
  },
  {
    id: "cash-flow",
    title: "Cash Flow Forecasting",
    icon: LineChart,
    description: "90-day projections and What-If scenarios",
    topics: ["90-Day Forecast", "Shortfall Detection", "What-If Simulator"]
  },
  {
    id: "debt",
    title: "Debt Payoff Planning",
    icon: CreditCard,
    description: "Create a strategy to become debt-free",
    topics: ["Adding Debts", "Payoff Strategies", "Extra Payments"]
  },
  {
    id: "courses",
    title: "Financial Courses",
    icon: GraduationCap,
    description: "Learn personal finance with free courses",
    topics: ["Available Courses", "Progress Tracking", "Earning Badges"]
  },
  {
    id: "household",
    title: "Household Sharing",
    icon: Users,
    description: "Share finances with family members",
    topics: ["Creating Household", "Joining", "What's Shared"],
    isNew: true
  },
  {
    id: "settings",
    title: "Settings & Account",
    icon: Settings,
    description: "Manage your account and preferences",
    topics: ["Account Settings", "Notifications", "Connected Accounts"]
  },
  {
    id: "pricing",
    title: "Subscription & Pricing",
    icon: CreditCard,
    description: "Plans, pricing, and billing information",
    topics: ["Plans Overview", "Starting Trial", "Managing Subscription"]
  },
  {
    id: "privacy",
    title: "Privacy & Security",
    icon: Shield,
    description: "How we protect your data",
    topics: ["Data Protection", "Plaid Security", "Your Rights"]
  },
  {
    id: "faq",
    title: "FAQ & Troubleshooting",
    icon: HelpCircle,
    description: "Common questions and solutions",
    topics: ["Common Questions", "Troubleshooting", "Contact Support"]
  }
];

export const DocsPage = () => {
  const [searchQuery, setSearchQuery] = useState("");
  
  const filteredSections = useMemo(() => {
    if (!searchQuery.trim()) {
      return docSections;
    }
    const query = searchQuery.toLowerCase();
    return docSections.filter(section => 
      section.title.toLowerCase().includes(query) ||
      section.description.toLowerCase().includes(query) ||
      section.topics.some(topic => topic.toLowerCase().includes(query))
    );
  }, [searchQuery]);

  useEffect(() => {
    // Page title handled globally by useSEO (utils/seo.js)
  }, []);

  return (
    <MarketingLayout>

      {/* Hero */}
      <section className="bg-gradient-to-b from-primary/5 to-background py-16">
        <div className="container mx-auto px-4 text-center">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center">
              <BookOpen className="w-8 h-8 text-primary" />
            </div>
          </div>
          <h1 className="font-heading text-4xl font-bold mb-4">User Manual</h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
            Everything you need to know about using Tally to manage your finances
          </p>
          
          {/* Search */}
          <div className="max-w-md mx-auto relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search documentation..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 h-12 rounded-full"
            />
          </div>
        </div>
      </section>

      {/* Quick Links */}
      <section className="container mx-auto px-4 py-8">
        <div className="flex flex-wrap justify-center gap-2 mb-8">
          <Link to="/docs/getting-started">
            <Badge variant="outline" className="cursor-pointer hover:bg-primary hover:text-primary-foreground transition-colors">
              Getting Started
            </Badge>
          </Link>
          <Link to="/docs/bank-connection">
            <Badge variant="outline" className="cursor-pointer hover:bg-primary hover:text-primary-foreground transition-colors">
              Bank Connection
            </Badge>
          </Link>
          <Link to="/docs/budget">
            <Badge variant="outline" className="cursor-pointer hover:bg-primary hover:text-primary-foreground transition-colors">
              Budgeting
            </Badge>
          </Link>
          <Link to="/docs/bills">
            <Badge variant="outline" className="cursor-pointer hover:bg-primary hover:text-primary-foreground transition-colors">
              Bills
            </Badge>
          </Link>
          <Link to="/docs/insights">
            <Badge variant="outline" className="cursor-pointer hover:bg-primary hover:text-primary-foreground transition-colors">
              AI Features
            </Badge>
          </Link>
          <Link to="/docs/faq">
            <Badge variant="outline" className="cursor-pointer hover:bg-primary hover:text-primary-foreground transition-colors">
              FAQ
            </Badge>
          </Link>
        </div>
      </section>

      {/* Documentation Sections */}
      <section className="container mx-auto px-4 pb-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredSections.map((section) => {
            const Icon = section.icon;
            return (
              <Link to={`/docs/${section.id}`} key={section.id}>
                <Card 
                  className="border-border/50 hover:border-primary/50 hover:shadow-lg transition-all cursor-pointer group h-full"
                >
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                        <Icon className="w-6 h-6 text-primary" />
                      </div>
                      {section.isNew && (
                        <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
                          New
                        </Badge>
                      )}
                    </div>
                    <h3 className="font-heading font-semibold text-lg mb-2 group-hover:text-primary transition-colors">
                      {section.title}
                    </h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      {section.description}
                    </p>
                    <div className="space-y-1">
                      {section.topics.slice(0, 3).map((topic, idx) => (
                        <div key={idx} className="flex items-center gap-2 text-sm text-muted-foreground">
                          <ChevronRight className="w-3 h-3" />
                          <span>{topic}</span>
                        </div>
                      ))}
                      {section.topics.length > 3 && (
                        <div className="text-xs text-muted-foreground pl-5">
                          +{section.topics.length - 3} more topics
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>

        {filteredSections.length === 0 && (
          <div className="text-center py-12">
            <p className="text-muted-foreground">No results found for &quot;{searchQuery}&quot;</p>
            <Button 
              variant="ghost" 
              onClick={() => setSearchQuery("")}
              className="mt-2"
            >
              Clear search
            </Button>
          </div>
        )}
      </section>

      {/* Full Manual Link */}
      <section className="container mx-auto px-4 pb-16">
        <Card className="border-border/50 bg-gradient-to-r from-primary/5 to-transparent">
          <CardContent className="p-8 text-center">
            <h2 className="font-heading text-2xl font-bold mb-2">Full User Manual</h2>
            <p className="text-muted-foreground mb-6">
              Read the complete documentation in one page
            </p>
            <Button asChild className="rounded-full">
              <a href="/docs/user-manual.md" target="_blank" rel="noopener noreferrer">
                <ExternalLink className="mr-2 w-4 h-4" />
                Open Full Manual
              </a>
            </Button>
          </CardContent>
        </Card>
      </section>

      {/* Footer */}
    </MarketingLayout>
  );
};

export default DocsPage;
